// PAQUETES
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
// componentes
import HelmetSEO from '../../componentesEspeciales/helmetSEO';
import PanelBusquedaServicios from '../../componentesEspeciales/busqueda/panelBusquedaServicios';
import useBusquedaServicios from '../../componentesEspeciales/busqueda/useBusquedaServicios';

function Busqueda() {
  const navigate = useNavigate();
  const location = useLocation();
  const { usuario, cargandoUsuario } = useOutletContext() || {};

  const busqueda = useBusquedaServicios({ idUsuario: usuario?.idUsuario || null });
  const {
    palabra, setPalabra,
    ciudad, setCiudad,
    categoria, setCategoria,
    subcategoria, setSubcategoria,
    ordenar, setOrdenar,
    precioMax, setPrecioMax,
    setPage,
    ejecutarBusqueda,
    limpiarFiltros,
    categoriasConfig,
    setVista,
  } = busqueda;

  // Cuando el usuario elige una categoría, mostramos su ícono y su imagen de fondo específica
  // en el hero (igual que en /categoria/:categoria), en vez del carrusel genérico de todas.
  const categoriaSeleccionadaConfig = (categoriasConfig || []).find((c) => c.categoria === categoria) || null;

  const [filtrosHidratados, setFiltrosHidratados] = useState(false);
  const busquedaInicialHecha = useRef(false);
  const ejecutarBusquedaPorNavegacionRef = useRef(false);

  // -------------------------------
  // RECIBIR FILTROS INTERNOS
  //    navigate("/busqueda", { state: { filtros: { palabra, ciudad, categoria, subcategoria }}})
  // -------------------------------
  useEffect(() => {
    const filtros = location?.state?.filtros;
    // Si no hay filtros, igual marcamos como hidratado para poder disparar búsqueda inicial.
    if (!filtros) {
      setFiltrosHidratados(true);
      return;
    }

    ejecutarBusquedaPorNavegacionRef.current = true;

    // Solo asigna si viene definido; si no, deja null.
    if (typeof filtros.palabra !== "undefined") setPalabra(filtros.palabra);
    if (typeof filtros.ciudad !== "undefined") setCiudad(filtros.ciudad);
    if (typeof filtros.categoria !== "undefined") setCategoria(filtros.categoria);
    if (typeof filtros.subcategoria !== "undefined") setSubcategoria(filtros.subcategoria);
    if (typeof filtros.ordenar !== "undefined") setOrdenar(filtros.ordenar);
    if (typeof filtros.precioMax !== "undefined") setPrecioMax(filtros.precioMax);
    // Permite que Home (u otra página) navegue directo a la vista mapa ya filtrada por ciudad.
    if (typeof filtros.vista !== "undefined") setVista(filtros.vista);

    // Importante: resetea a página 1 cuando llega una búsqueda desde navegación
    setPage(1);
    busquedaInicialHecha.current = false;

    setFiltrosHidratados(true);
  }, [location?.state]);

  // -------------------------------
  // BÚSQUEDA INICIAL (al cargar la página)
  // -------------------------------
  useEffect(() => {
    // Espera a que Direccionamiento termine de cargar sesión y a que los filtros internos se hidraten.
    if (cargandoUsuario) return;
    if (!filtrosHidratados) return;

    // Cuando llegamos desde otra página con nuevos filtros internos,
    // ejecutamos la búsqueda automáticamente con página 1.
    if (ejecutarBusquedaPorNavegacionRef.current) {
      ejecutarBusquedaPorNavegacionRef.current = false;
      busquedaInicialHecha.current = true;
      ejecutarBusqueda({ pageOverride: 1 });
      return;
    }

    // Primera carga de la página sin navegación interna previa.
    if (busquedaInicialHecha.current) return;

    busquedaInicialHecha.current = true;
    ejecutarBusqueda();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargandoUsuario, filtrosHidratados, palabra, ciudad, categoria, subcategoria, ordenar, precioMax]);

  function limpiarFiltrosPagina() {
    // Limpia el state interno (para que no se rehidrate nada al volver)
    navigate("/busqueda", { replace: true, state: {} });
    limpiarFiltros();
  }

  return (
    <>
      {/*
        SEO dinámico de la búsqueda.
        Cambia automáticamente:
        - título
        - descripción SEO
        - Open Graph
        según filtros activos.
      */}
      <HelmetSEO
        titulo={
          categoria && ciudad
            ? `${categoria} en ${ciudad} | Zervicy`
            : categoria
              ? `${categoria} | Zervicy`
              : palabra
                ? `${palabra} | Zervicy`
                : "Búsqueda de servicios | Zervicy"
        }

        descripcion={
          categoria && ciudad
            ? `Encuentra ${categoria.toLowerCase()} en ${ciudad} de forma rápida y segura en Zervicy.`
            : categoria
              ? `Explora servicios de ${categoria.toLowerCase()} disponibles en Zervicy.`
              : palabra
                ? `Resultados para ${palabra} en Zervicy.`
                : "Encuentra servicios profesionales, reservas rápidas y proveedores verificados en Zervicy."
        }
      />

      <PanelBusquedaServicios
        busqueda={busqueda}
        usuario={usuario}
        cargandoUsuario={cargandoUsuario}
        titulo="Búsqueda"
        subtitulo="Filtra por categoría, subcategoría, ciudad o palabra clave. Todo desde una sola página."
        textoIntro="Explora proveedores verificados, compara precios y reserva servicios de forma segura en toda Colombia."
        mensajeSinResultados="Ajusta filtros o haz una búsqueda. Esta página es única y recibe filtros internos."
        onLimpiarFiltros={limpiarFiltrosPagina}
        icono={categoriaSeleccionadaConfig?.icono}
        imagenesHero={categoriaSeleccionadaConfig?.imagen ? [categoriaSeleccionadaConfig.imagen] : undefined}
        mostrarBarraConfianza
        mostrarChipsSubcategoria
        mostrarBotonCercaDeMi
      />
    </>
  );
}

export default Busqueda;
